import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { InventoryRule, Marketplace } from "@front/m19-api-client";
import {
  IAlertComponent,
  IButtonDirective,
  ICheckboxComponent,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  IRadioComponent,
  IRadioGroupComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { UntilDestroy } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: "app-asin-inventory-rules-modal",
  templateUrl: "./asin-inventory-rules-modal.component.html",
  styleUrls: ["./asin-inventory-rules-modal.component.scss"],
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    ICheckboxComponent,
    IButtonDirective,
    ITooltipDirective,
    ILabelComponent,
    IRadioComponent,
    IRadioGroupComponent,
    FormsModule,
    IFormFieldComponent,
    IAlertComponent,
    IInputDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsinInventoryRuleModalComponent implements OnInit {
  modalData = inject<AsinInventoryRulesModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);
  private readonly destroyRef = inject(DestroyRef);

  ruleForm = new FormGroup({
    hasRule: new FormControl<boolean>(!!this.modalData.rule, { nonNullable: true }),
    pauseThreshold: new FormControl<number | null>(
      {
        value: this.modalData.rule?.advertisingPauseThreshold ?? null,
        disabled: !this.modalData.rule,
      },
      {
        validators: [Validators.required, Validators.min(1), Validators.pattern(/^[0-9]+$/)],
      },
    ),
    activateAdvertisingWhenInbound: new FormControl<boolean>(!!this.modalData.rule?.activateAdvertisingWhenInbound, {
      nonNullable: true,
    }),
    onlyFba: new FormControl<boolean>(!!this.modalData.rule?.onlyFba, {
      nonNullable: true,
    }),
  });

  get pauseThreshold(): FormControl {
    return this.ruleForm.controls.pauseThreshold;
  }

  get pausingRule(): AbstractControl {
    return this.ruleForm.controls.hasRule;
  }

  get activateWhenInbound(): AbstractControl {
    return this.ruleForm.controls.activateAdvertisingWhenInbound;
  }

  ngOnInit() {
    this.ruleForm.controls.hasRule.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hasRule) => {
      if (hasRule) this.ruleForm.controls.pauseThreshold.enable();
      else this.ruleForm.controls.pauseThreshold.disable();
    });
    this.ruleForm.updateValueAndValidity();
  }

  save(): void {
    if (!this.pausingRule.value) {
      this.modalRef.close("delete");
      return;
    }
    this.modalRef.close({
      accountId: this.modalData.accountId,
      marketplace: this.modalData.marketplace,
      asin: this.modalData.asin,
      advertisingPauseThreshold: this.pauseThreshold.value,
      activateAdvertisingWhenInbound: this.activateWhenInbound.value,
      onlyFba: this.ruleForm.controls.onlyFba.value,
    });
  }
}

export interface AsinInventoryRulesModalData {
  accountId: string;
  marketplace: Marketplace;
  asin?: string;
  rule?: InventoryRule;
  warningMessage?: string;
}

export type AsinInventoryRulesOutput = InventoryRule | "delete";
